import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';

export type ToastVariant = 'success' | 'error';

export interface ToastMessage {
  id: number;
  message: string;
  variant: ToastVariant;
}

export interface ToastContextValue {
  toasts: ToastMessage[];
  addToast: (message: string, variant: ToastVariant) => void;
  removeToast: (id: number) => void;
}

let nextId = 1;

export const ToastContext = createContext<ToastContextValue | null>(null);

export function useToastState(): ToastContextValue {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, variant: ToastVariant) => {
      const id = nextId++;
      setToasts((prev) => [...prev, { id, message, variant }]);
      // Spec 230 (Kridt Toast recipe): neutral and success time out after
      // 5s; warning and danger stay until the person dismisses them. This
      // type only has 'success' and 'error' today — 'error' is the danger
      // tone in Toast.tsx's rendering and must persist.
      if (variant === 'success') {
        setTimeout(() => removeToast(id), 5000);
      }
    },
    [removeToast],
  );

  return { toasts, addToast, removeToast };
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
